import { useEffect, useMemo, type RefObject } from 'react';
import { useHotkeys, type HotkeyHandler } from './use-hotkeys';

/** One component's claim over a key. Identity is what resolves the winner. */
interface Claim {
  containerRef: RefObject<HTMLElement | null> | undefined;
}

/** Mount-ordered claims per key. Module scope on purpose: it spans the app. */
const claims = new Map<string, Claim[]>();

function claim(key: string, entry: Claim): () => void {
  const list = claims.get(key) ?? [];
  list.push(entry);
  claims.set(key, list);

  return () => {
    const current = claims.get(key);
    if (!current) return;
    const index = current.indexOf(entry);
    if (index >= 0) current.splice(index, 1);
    if (current.length === 0) claims.delete(key);
  };
}

/**
 * A claim whose element is gone belongs to a component that is no longer
 * listening, so handing it the key means nobody answers.
 *
 * A claim without a container is page-wide (`c` from `useCreateAction`) and has
 * no element to check, so it stays eligible.
 */
function isLive(entry: Claim): boolean {
  if (!entry.containerRef) return true;
  const node = entry.containerRef.current;
  return node !== null && node.isConnected;
}

/**
 * Who answers this keystroke.
 *
 * The newest claim whose container holds the event target wins, so a list
 * inside a drawer answers for the drawer while the page keeps answering for
 * the page. With the focus nowhere in particular — on `<body>` right after a
 * navigation — the last mounted claim answers, which is the one on top.
 *
 * That fallback is where `/` went to die: a claim left behind by a component
 * that had already stopped listening still won the arbitration, so the live
 * FilterBar saw `owner !== entry`, declined, and the keystroke reached the
 * document and was consumed by no one. Skipping detached claims makes the
 * fallback self-healing; when every claim is live the winner is unchanged.
 */
function owner(key: string, target: EventTarget | null): Claim | undefined {
  const list = claims.get(key);
  if (!list || list.length === 0) return undefined;

  if (target instanceof Node) {
    for (let index = list.length - 1; index >= 0; index -= 1) {
      const entry = list[index];
      if (entry?.containerRef?.current?.contains(target)) return entry;
    }
  }

  for (let index = list.length - 1; index >= 0; index -= 1) {
    const entry = list[index];
    if (entry && isLive(entry)) return entry;
  }

  return list[list.length - 1];
}

export interface ScopedHotkeyOptions {
  /**
   * The region this component answers for. Two views on screen at once —
   * master-detail, or a list inside a drawer — pass their own container so the
   * keystroke lands where the user is looking instead of on whoever mounted
   * last.
   */
  containerRef?: RefObject<HTMLElement | null>;
  enabled?: boolean;
}

/**
 * A global key with exactly one owner at a time.
 *
 * `useHotkeys` alone listens on the document, so every mounted copy of a
 * component would react to the same keystroke and fight over the result. This
 * arbitrates first and runs the handler in one place only.
 */
export function useScopedHotkey(
  key: string,
  handler: HotkeyHandler,
  options: ScopedHotkeyOptions = {},
): void {
  const { containerRef, enabled = true } = options;
  const entry = useMemo<Claim>(() => ({ containerRef }), [containerRef]);

  useEffect(() => {
    if (!enabled) return;
    return claim(key, entry);
  }, [key, entry, enabled]);

  useHotkeys(
    {
      [key]: (event: KeyboardEvent) => {
        if (owner(key, event.target) === entry) handler(event);
      },
    },
    { enabled },
  );
}
